import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { grantPolicyReportingCompliance } from '../content/educational/grantPolicyReportingCompliance';

const processKeywords = (text, keywords) => {
  if (!keywords || !Array.isArray(keywords)) return text;
  
  let processedText = text;
  keywords.forEach(keyword => {
    if (keyword && typeof keyword === 'string') {
      const regex = new RegExp(`(${keyword})`, 'gi');
      processedText = processedText.replace(regex, '**$1**');
    }
  });
  return processedText;
};

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;

    // Font sizes
    const fonts = {
      title: 22,
      sectionHeading: 14,
      subHeading: 12,
      body: 11,
      footer: 8
    };

    // Colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255]
    };

    // Generate QR Codes
    const qrCodeAcademy = await QRCode.toDataURL('https://causeandimpact.org/academy');
    const qrCodeDonate = await QRCode.toDataURL('https://causeandimpact.org/donate');

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(...colors.blue);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(...colors.green);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(...colors.white);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeAcademy, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.footer);
      doc.setTextColor(...colors.white);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Add markdown processing
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      // Special handling for checklist items
      if (text.startsWith("'") || text.startsWith('✓')) {
        return [{
          text: text.trim(),
          bold: false
        }];
      }
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Enhanced text rendering with proper spacing
    const renderTextWithBold = (text, x, y, isExample = false) => {
      const segments = processMarkdown(text);
      let currentX = x;
      
      segments.forEach(segment => {
        doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
        
        // Handle examples in green
        if (isExample && segment.text.includes('Example:')) {
          const parts = segment.text.split('Example:');
          if (parts.length > 1) {
            doc.setTextColor(...colors.gray);
            doc.text('Example:', currentX, y);
            currentX += doc.getTextWidth('Example:');
            
            doc.setTextColor(...colors.green);
            doc.text(parts[1], currentX, y);
            currentX += doc.getTextWidth(parts[1]);
          }
        } else {
          doc.setTextColor(...colors.gray);
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        }
      });
    };

    // Update the processList function with better page break and font size handling
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 12;
      const subIndent = 8;

      // Define consistent sizes that won't change
      const SIZES = {
        HEADER: 14,
        SUBHEADER: 12,
        BODY: 11,
        BULLET: 11
      };

      // Add checklist specific settings
      const CHECKLIST = {
        INDENT: 15,
        SPACING: 6,
        SYMBOL: '✓'
      };

      // Add to font settings
      const fontSettings = {
        header: { size: SIZES.HEADER, style: 'bold', color: colors.green },
        body: { size: SIZES.BODY, style: 'normal', color: colors.gray },
        bullet: { size: SIZES.BULLET, style: 'normal', color: colors.gray },
        checklist: { size: SIZES.BODY, style: 'normal', color: colors.gray }
      };

      // Track current font state
      let currentFontState = {
        size: SIZES.BODY,
        style: 'normal',
        color: colors.gray
      };

      // Enhanced font state management
      const setFontState = (state, force = false) => {
        if (force || 
            state.size !== currentFontState.size || 
            state.style !== currentFontState.style || 
            state.color !== currentFontState.color) {
          currentFontState = { ...state };
          doc.setFont('helvetica', state.style);
          doc.setFontSize(state.size);
          doc.setTextColor(...state.color);
        }
      };

      // Enhanced page break handler
      const checkPageBreak = (height = 4) => {
        if (currentY + height > pageHeight - footerHeight - 15) {
          const savedState = { ...currentFontState };
          
          doc.addPage();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
          currentY = headerHeight + 12;
          
          // Force font state restoration after page break
          setFontState(savedState, true);
          return true;
        }
        return false;
      };

      // Enhanced text rendering with size control
      const renderFormattedText = (text, x, y, size = SIZES.BODY) => {
        const segments = processMarkdown(text);
        let currentX = x;
        
        segments.forEach(segment => {
          const fontStyle = segment.bold ? 'bold' : currentFontState.style;
          doc.setFont('helvetica', fontStyle);
          doc.setFontSize(size); // Explicitly set size for each segment
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        
        // Restore font state
        setFontState(currentFontState, true);
      };

      // Add checklist item handler
      const renderChecklistItem = (text, x, y) => {
        setFontState(fontSettings.checklist, true);
        doc.text('✓', x, y);
        renderFormattedText(text, x + CHECKLIST.INDENT, y, SIZES.BODY);
      };

      // Process each line
      lines.forEach(line => {
        const trimmedLine = line.trim();

        // Handle section headers with special case for checklist headers
        if (trimmedLine.startsWith('###')) {
          checkPageBreak(8);
          setFontState(fontSettings.header);
          
          // Special handling for checklist headers
          if (trimmedLine.toLowerCase().includes('checklist')) {
            const headerText = trimmedLine.replace('###', '').trim();
            doc.setTextColor(...colors.green);
            doc.text(headerText, margin, currentY);
            currentY += 8;
            setFontState(fontSettings.body);
            return;
          }
          
          // Regular header handling
          doc.text(trimmedLine.replace('###', '').trim(), margin, currentY);
          currentY += 8;
          setFontState(fontSettings.body);
          return;
        }

        // Handle checklist items (starting with single quote or checkmark)
        if (trimmedLine.startsWith("'") || trimmedLine.startsWith('✓')) {
          const itemText = trimmedLine.substring(1).trim();
          checkPageBreak(6);
          renderChecklistItem(itemText, margin + 4, currentY);
          currentY += CHECKLIST.SPACING;
          return;
        }

        // Handle bullet points with consistent sizing
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
          const bulletText = trimmedLine.substring(1).trim();
          checkPageBreak(6);
          
          // Force consistent bullet point formatting
          setFontState(fontSettings.bullet, true);
          
          const indent = trimmedLine.startsWith('   ') ? bulletIndent + subIndent : bulletIndent;
          
          // Draw bullet with consistent size
          doc.setFontSize(SIZES.BULLET);
          doc.text('•', margin + indent - 4, currentY);
          
          const textLines = doc.splitTextToSize(bulletText, contentWidth - (indent + 8));
          textLines.forEach((line, index) => {
            if (index > 0) {
              currentY += 5;
              checkPageBreak(5);
              setFontState(fontSettings.bullet, true); // Force size restoration
            }
            renderFormattedText(line, margin + indent + 4, currentY, SIZES.BULLET);
          });
          
          currentY += 6;
          return;
        }

        // Handle regular text
        if (trimmedLine) {
          setFontState(fontSettings.body);
          
          const textLines = doc.splitTextToSize(trimmedLine, contentWidth);
          textLines.forEach(line => {
            renderFormattedText(line, margin, currentY, SIZES.BODY);
            currentY += 5;
          });
        } else {
          currentY += 3;
        }
      });

      return currentY;
    };

    // Add Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 28;
      
      // Add a subtle gradient effect for the main CTA box
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 6, 6, 'F');
      
      // Main CTA text with better positioning
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...colors.white);
      doc.text('Found this helpful?', pageWidth / 2, yPosition + 18, { align: 'center' });
      
      // Subtitle with call to action
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Scan to donate to one of our many Causes.', pageWidth / 2, yPosition + 28, { align: 'center' });
      
      // Enhanced QR code presentation with better alignment
      const qrX = pageWidth - qrSize - margin - 6;
      const qrY = yPosition + 8;
      
      // Add layered shadow effect for depth (adjusted offsets)
      doc.setFillColor(0, 0, 0, 0.06);
      doc.roundedRect(qrX + 0.8, qrY + 0.8, qrSize, qrSize, 2, 2, 'F');
      doc.setFillColor(0, 0, 0, 0.04);
      doc.roundedRect(qrX + 0.4, qrY + 0.4, qrSize, qrSize, 2, 2, 'F');
      
      // Add white background for QR code
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX, qrY, qrSize, qrSize, 2, 2, 'F');
      
      // Add the QR code image (aligned with background)
      doc.addImage(qrCodeDonate, 'PNG', qrX, qrY, qrSize, qrSize);
    };

    // Content generation with enhanced spacing
    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title
      doc.setFontSize(fonts.title);
      doc.setTextColor(...colors.blue);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      yPosition += (titleLines.length * 10) + 15;

      // Process each section
      content.forEach((section, index) => {
        // Check if section will fit
        if (yPosition + 50 > pageHeight - footerHeight - 15) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.setTextColor(...colors.green);
        doc.text(section.heading, margin, yPosition);
        yPosition += 12;

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);

        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          // Check for page break before paragraph
          if (yPosition + 50 > pageHeight - footerHeight - 15) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }

          yPosition = processList(paragraph, yPosition);
          yPosition += 6;
        });

        yPosition += 15;
      });

      // Add CTA if space allows
      if (yPosition + 50 > pageHeight - footerHeight - 15) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 12;
      }

      yPosition += 8;
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata
    doc.setProperties({
      title: title,
      subject: 'Grant Management and Compliance Guide',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact',
      producer: 'Cause & Impact Grant Management System',
      description: 'Comprehensive guide for post-award grant management excellence and compliance.',
      modDate: new Date()
    });

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(`PDF Generation Error: ${error.message}`);
  }
};

export const generateGrantPolicyReportingOverview = async () => {
  try {
    const doc = await generatePDF(
      grantPolicyReportingCompliance.sections,
      grantPolicyReportingCompliance.title,
      grantPolicyReportingCompliance.keywords
    );
    
    if (!doc) {
      throw new Error('PDF generation failed - null document returned');
    }
    
    return doc;
  } catch (error) {
    console.error('Error in generateGrantPolicyReportingOverview:', error);
    throw error;
  }
}; 